import {FatGreenOutlineButton} from 'common';
import {Pane} from 'evergreen-ui';
import {Offer} from 'offer';
import React, {memo, useState} from 'react';
import {decoupleServiceType, Referral, ServiceType} from 'referral';
import {BehaviorSubject, combineLatest, Observable, Subject} from 'rxjs';
import {map} from 'rxjs/operators';
import {ErrorDialog} from '../../common/component/ErrorDialog';
import {OfferForm} from './OfferForm';
import {OfferFormHeader} from './OfferFormHeader';
import {
    AutocompleteProviderByReferralInput
} from "../../usermanagementapp/component/ProviderUserForm/component/AutocompleteProviderByReferralInput";
import {useSubmitNewOfferClientPlacement$} from "../hook/useSubmitNewOfferClientPlacement$";
import {useGoToReferralListPageForPlacement} from "../../placement/routes";
import {useObservable} from "rxsupport";


type NewOfferFormProps = {
    referral: Referral;
    excludedProviderSfIdsFromPref: string[];
    successCallback: (newOffer: Partial<Offer>) => any;
};
export const NewOfferFormForClientPlacement = memo(({
                                                        excludedProviderSfIdsFromPref,
                                                        referral,
                                                        successCallback,
                                                    }: NewOfferFormProps
) => {

    const {execute: submitOperation, isRunning$: isSubmitting$, errorMessage$} = useSubmitNewOfferClientPlacement$(
        referral.guid,
        (offer) => successCallback(offer)
    );

    const [shouldValidateSignal$] = useState(() => new Subject<boolean>());
    const [validationResults$] = useState(() => new BehaviorSubject<boolean>(false));

    const [submitSignal$] = useState<Observable<boolean>>(() =>
        combineLatest([
            shouldValidateSignal$,
            validationResults$,
        ]).pipe(
            map(value => value[0] || value[1])
        ));

    const goToReferralListPage = useGoToReferralListPageForPlacement();
    const isSubmitting = useObservable(isSubmitting$, false);

    return (
        <Pane
            className="max-h-full h-full box-border portal-provider-offer-form-container flex flex-col overflow-y-scroll">
            <OfferFormHeader caseNumber={referral.caseNumber} hasCloseBtn={false} onClose={goToReferralListPage}/>

            <OfferForm referral={referral}
                       existingOffer={{
                           comments: 'Offer submitted by CCS on behalf of Provider'
                       }}
                       validatePlease$={shouldValidateSignal$ as Observable<boolean>}
                       validationResult$={validationResults$}

                       submitPlease$={submitSignal$}
                       isSubmitting$={isSubmitting$}
                       submitOperation={submitOperation}
                       providerInput={<AutocompleteProviderByReferralInput
                           referralPreferredLocation={extractReferralPreferLocation(referral)}
                           referralPreferredServiceTypes={extractReferralDecoupleServiceType(referral)}
                           excludedProviderSfIds={excludedProviderSfIdsFromPref}
                           isSubmitting$={isSubmitting$}/>}
            />

            <Pane className='portal-provider-offer-form-actions portal-provider-bg flex justify-evenly'>
                <Pane width={200}>
                    <FatGreenOutlineButton text='Cancel' type="button" onClick={goToReferralListPage}
                                           disabled={isSubmitting}/>
                </Pane>
                <Pane width={200}>
                    <FatGreenOutlineButton text='Submit Offer' type='button'
                                           onClick={() => shouldValidateSignal$.next(true)}
                                           disabled={isSubmitting}/>
                </Pane>
            </Pane>

            <ErrorDialog message$={errorMessage$}/>
        </Pane>
    );
});

const extractReferralPreferLocation = (referral: Referral) => {
    const location = referral.location;
    return extractCity(location)?.trim()?.toLowerCase() ?? null;
}

const extractCity = (location: string): string | null => {
    if (!location) return null;

    const parts = location.split(' ');
    return parts.length > 1 ? parts.slice(0, -1).join(' ') : null;
}


const extractReferralDecoupleServiceType = (referral: Referral): ServiceType[] => {
    if (referral.serviceTypes === null) {
        return [];
    }
    return referral.serviceTypes.map((serviceType) => decoupleServiceType(serviceType)).flat();
}
